/**
 * Link Recommendations Page
 * Displays and manages link recommendations - matches reference implementation
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Link2, 
  X, 
  Sparkles, 
  Loader2,
  AlertTriangle,
  Eye,
  Check,
  Trash2,
  Info,
  ExternalLink,
  Pencil,
  RotateCcw,
  Tag,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Copy
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSite } from '../../hooks/useSite';
import { 
  linksApi, 
  structureApi,
  type LinkRecommendation, 
  type LinkRecommendationStats,
  type StructureAnalysis,
  type StructureState,
  type PrimaryTagRecommendation,
} from '../../lib/dashboard-api';

// Short definitions for tooltips (from reference implementation)
// These show on hover over the relationship type badge
const shortDefinitions: Record<string, string> = {
  'conceptual_hierarchy': 'Parent-child or general-to-specific relationship between concepts',
  'thematic_grouping': 'Posts sharing common themes or topics without strict hierarchy',
  'methodology_application': 'How a method or framework applies to a specific context',
  'prerequisite_foundation': 'Required knowledge needed before progressing to advanced topics',
  'supporting_evidence': 'Data, research, or examples that validate a concept',
  'tool_ecosystem': 'Related tools that work together or complement each other',
  'implementation_cascade': 'Sequential steps that build upon each other',
  'problem_solution_cluster': 'Problems paired with their solutions',
  'problem_solution': 'Problems paired with their solutions',
  'expertise_bridge': 'Transfers knowledge from one domain to help understand another',
  'integration_pattern': 'How systems or components connect and work together',
  'skill_progression': 'Learning path from beginner to advanced levels',
  'comparative_analysis': 'Compares different approaches or solutions to similar problems',
  'strategic_alignment': 'Different tactics working toward common strategic goals'
};

// The 13 Standard Semantic Relationship Types
// From: Specifications/VizzEx-Semantic-Relationship-Types-Definitions.md
const relationshipDefs: Record<string, { 
  title: string;
  definition: string; 
  examples: string[]; 
  whenToUse: string;
}> = {
  'conceptual_hierarchy': {
    title: 'Conceptual Hierarchy',
    definition: 'A parent-child or general-to-specific relationship where one concept encompasses or categorizes another. Shows how ideas nest within broader frameworks.',
    examples: [
      '"Machine learning is a subset of artificial intelligence"',
      '"This is a type of..."',
      '"Y is a specialized form of Z"',
      '"This approach builds on the foundational concept of..."'
    ],
    whenToUse: 'Linking from specific implementation to general principle, connecting specialized topics to their broader category.'
  },
  'thematic_grouping': {
    title: 'Thematic Grouping',
    definition: 'Items that share common themes, topics, or subject matter but aren\'t necessarily hierarchical. These posts explore similar ideas without a strict parent-child relationship.',
    examples: [
      '"Both articles discuss climate change adaptation"',
      '"Similarly themed content includes..."',
      '"This aligns with our discussion of..."'
    ],
    whenToUse: 'Connecting posts that share a common theme, grouping content around similar topics without implying hierarchy.'
  },
  'methodology_application': {
    title: 'Methodology Application',
    definition: 'How a specific method, framework, or approach is applied to a domain, problem, or context. Shows practical application of theoretical frameworks.',
    examples: [
      '"Applying agile methodology to marketing campaigns"',
      '"This framework can be applied to..."',
      '"The same methodology we used for X works well with Y"'
    ],
    whenToUse: 'Linking methodology explanation to practical application, showing how an approach transfers across domains.'
  },
  'prerequisite_foundation': {
    title: 'Prerequisite Foundation',
    definition: 'Knowledge, skills, or concepts that must be understood before progressing to another topic. Establishes foundational learning required for advanced material.',
    examples: [
      '"Before learning React, you should understand JavaScript"',
      '"Prerequisites include..."',
      '"First, make sure you understand..."'
    ],
    whenToUse: 'Linking from advanced content back to foundational concepts, establishing learning dependencies.'
  },
  'supporting_evidence': {
    title: 'Supporting Evidence',
    definition: 'Data, research, examples, or documentation that validates or reinforces a concept or claim. Provides proof points and empirical backing.',
    examples: [
      '"Research shows that..."',
      '"As documented in..."',
      '"Case studies demonstrate..."'
    ],
    whenToUse: 'Linking claims to supporting data, connecting theoretical concepts to real-world proof.'
  },
  'tool_ecosystem': {
    title: 'Tool Ecosystem',
    definition: 'Related tools, technologies, or platforms that work together or serve complementary purposes in a workflow.',
    examples: [
      '"This integrates with Slack, Jira, and GitHub"',
      '"Pairs well with..."',
      '"Common companions to this tool are..."'
    ],
    whenToUse: 'Connecting tools that integrate or complement each other, building out technology stacks.'
  },
  'implementation_cascade': {
    title: 'Implementation Cascade',
    definition: 'Sequential steps, phases, or stages that build upon each other in implementation. Shows ordered progression of actions needed to complete a process.',
    examples: [
      '"First, set up authentication, then configure the database"',
      '"Once you\'ve completed this step, move on to..."',
      '"The next phase involves..."'
    ],
    whenToUse: 'Linking sequential implementation steps, creating ordered tutorials, showing process dependencies.'
  },
  'problem_solution_cluster': {
    title: 'Problem-Solution Cluster',
    definition: 'Problems paired with their solutions, or challenges linked to approaches that address them. Creates direct connections between issues and resolutions.',
    examples: [
      '"To solve X, we use Y"',
      '"If you\'re experiencing X, try Y"',
      '"This resolves..."'
    ],
    whenToUse: 'Linking problem identification to solution articles, creating problem-solving pathways.'
  },
  // Alias for problem_solution_cluster
  'problem_solution': {
    title: 'Problem-Solution Cluster',
    definition: 'Problems paired with their solutions, or challenges linked to approaches that address them. Creates direct connections between issues and resolutions.',
    examples: [
      '"To solve X, we use Y"',
      '"If you\'re experiencing X, try Y"',
      '"This resolves..."'
    ],
    whenToUse: 'Linking problem identification to solution articles, creating problem-solving pathways.'
  },
  'expertise_bridge': {
    title: 'Expertise Bridge',
    definition: 'Connections that transfer knowledge or skills from one domain to help understand another. Leverages existing expertise through analogies.',
    examples: [
      '"If you understand accounting principles, you\'ll grasp blockchain ledgers"',
      '"Think of this like X, which you already know"',
      '"The same concept applies in..."'
    ],
    whenToUse: 'Connecting concepts across different domains, helping readers leverage existing knowledge.'
  },
  'integration_pattern': {
    title: 'Integration Pattern',
    definition: 'How different systems, components, or concepts work together or connect in practice. Shows practical mechanics of interfaces and integration.',
    examples: [
      '"This API connects the frontend to the backend"',
      '"These components work together by..."',
      '"Bridges X and Y"'
    ],
    whenToUse: 'Showing how systems integrate, explaining interface points and integration patterns.'
  },
  'skill_progression': {
    title: 'Skill Progression',
    definition: 'A learning path or development trajectory from beginner to advanced levels. Establishes natural progression for skill development.',
    examples: [
      '"Start with HTML, then move to CSS, then JavaScript"',
      '"After learning the basics, you\'re ready for..."',
      '"Progress from... to..."'
    ],
    whenToUse: 'Creating learning pathways, linking beginner content to intermediate/advanced topics.'
  },
  'comparative_analysis': {
    title: 'Comparative Analysis',
    definition: 'Posts that compare different approaches, tools, methods, or solutions to similar problems. Highlights trade-offs and differences between alternatives.',
    examples: [
      '"Comparing React vs Vue for this use case"',
      '"When to choose A over B"',
      '"Versus analysis: X compared to Y"'
    ],
    whenToUse: 'Connecting comparison posts to detailed individual options, providing decision-making context.'
  },
  'strategic_alignment': {
    title: 'Strategic Alignment',
    definition: 'Posts that share overarching goals, objectives, or strategic direction. Shows how different tactics work toward common business objectives.',
    examples: [
      '"Both strategies support our goal of customer retention"',
      '"Different paths to the same destination"',
      '"Aligned with our core objective of..."'
    ],
    whenToUse: 'Connecting different tactical approaches to shared goals, creating strategic context across content.'
  }
};

// Normalize relationship type key
/**
 * Escape HTML special characters to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Build replacement HTML by wrapping anchor text with a link
 * Matches reference implementation's buildReplacementHTML function
 */
function buildReplacementHTML(
  paragraph: string | undefined, 
  anchorText: string | undefined, 
  targetUrl: string | undefined
): string {
  if (!paragraph) return '';
  
  // Check if paragraph already contains HTML link (from API)
  if (paragraph.includes('<a href=')) {
    return paragraph;
  }
  
  // No link embedded, we need to build it ourselves
  if (!anchorText || !targetUrl) {
    return escapeHtml(paragraph);
  }
  
  // Escape the paragraph first for safety
  const escapedParagraph = escapeHtml(paragraph);
  const escapedAnchor = escapeHtml(anchorText);
  
  // Build the HTML link
  const htmlLink = `<a href="${escapeHtml(targetUrl)}">${escapedAnchor}</a>`;
  
  // Replace the anchor text with the HTML link
  return escapedParagraph.replace(escapedAnchor, htmlLink);
}

function normalizeRelationshipType(type: string): string {
  return type.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
}

export default function LinksPage() {
  const { currentSite } = useAuth();
  const { isPro } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // HTML/No-CMS clients have no write-back capability — hide CMS-specific actions
  const isHtmlClient = currentSite?.cmsType === 'html' || currentSite?.platform === 'html';

  // Get postId filter from URL if present
  const postIdFilter = searchParams.get('postId');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<LinkRecommendation[]>([]);
  const [stats, setStats] = useState<LinkRecommendationStats | null>(null);
  const [categoryPairs, setCategoryPairs] = useState<string[]>([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('pending');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('priority_score');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Post title typeahead — seeded from the URL (e.g. arriving from the Post Links
  // "Search by Title/URL" tab's "View & Implement on Links Page" link, or from
  // Posts Requiring Attention). Selecting a result switches the page into the
  // existing postId-filtered view rather than doing a live text filter.
  const [postTitleInput, setPostTitleInput] = useState(searchParams.get('postTitle') || '');
  const [titleOptions, setTitleOptions] = useState<{ postId: string; title: string; linkCount: number }[]>([]);
  const [showTitleDropdown, setShowTitleDropdown] = useState(false);

  useEffect(() => {
    const trimmed = postTitleInput.trim();
    const selectedTitle = (searchParams.get('postTitle') || '').trim();

    // Don't re-search/reopen the dropdown for the title we're already filtered to
    // (covers both the initial page load from a URL param, and right after selecting).
    if (postIdFilter && trimmed === selectedTitle) {
      setTitleOptions([]);
      setShowTitleDropdown(false);
      return;
    }
    if (trimmed.length < 2 || !currentSite?.id) {
      setTitleOptions([]);
      setShowTitleDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await linksApi.searchPostTitles(currentSite.id, trimmed);
        setTitleOptions(res.posts || []);
        setShowTitleDropdown(true);
      } catch (err) {
        console.error('searchPostTitles error:', err);
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postTitleInput, currentSite?.id, postIdFilter]);

  const selectTitleOption = (opt: { postId: string; title: string; linkCount: number }) => {
    setPostTitleInput(opt.title);
    setShowTitleDropdown(false);
    setTitleOptions([]);
    const next = new URLSearchParams(searchParams);
    next.set('postId', opt.postId);
    next.set('postTitle', opt.title);
    setSearchParams(next);
  };

  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  
  // Expanded relationship info rows
  const [expandedRelRows, setExpandedRelRows] = useState<Set<number>>(new Set());
  
  const toggleRelationshipInfo = (id: number) => {
    console.log('toggleRelationshipInfo called with id:', id);
    console.log('Current expandedRelRows:', Array.from(expandedRelRows));
    setExpandedRelRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        console.log('Removing id from set:', id);
      } else {
        next.add(id);
        console.log('Adding id to set:', id);
      }
      console.log('New expandedRelRows:', Array.from(next));
      return next;
    });
  };

  // Semantic link modal
  const [semanticModal, setSemanticModal] = useState<{
    show: boolean;
    loading: boolean;
    recommendation: LinkRecommendation | null;
    error: string | null;
    // Structure analysis
    structureAnalysis: StructureAnalysis | null;
    structureAnalysisFailed: boolean;
    categoryRecommendation: PrimaryTagRecommendation | null;
    structureState: StructureState | null;
    sourcePostId: string | null;
    contentMd5: string | null;
    enableStructureAnalysis: boolean;
    enableCategoryRecommendation: boolean;
  }>({
    show: false,
    loading: false,
    recommendation: null,
    error: null,
    structureAnalysis: null,
    structureAnalysisFailed: false,
    categoryRecommendation: null,
    structureState: null,
    sourcePostId: null,
    contentMd5: null,
    enableStructureAnalysis: true,
    enableCategoryRecommendation: true,
  });

  // Per-row loading state for Done / Dismiss buttons (shows spinner while API call is in flight)
  const [rowActionPending, setRowActionPending] = useState<Record<number, 'done' | 'dismiss'>>({});
  // Brief success label shown after action completes, before status badge takes over
  const [rowActionFlash, setRowActionFlash] = useState<Record<number, 'done' | 'dismiss'>>({});

  // Tracks rec indices that have an in-flight action to prevent race conditions
  const pendingHeadingActions = useRef<Set<number>>(new Set());
  const [headingActionVersion, setHeadingActionVersion] = useState(0);
  const [headingApplyErrors, setHeadingApplyErrors] = useState<Record<number, string>>({});
  const [categoryApplying, setCategoryApplying] = useState(false);
  const [showCategoryConfirm, setShowCategoryConfirm] = useState(false);
  const [structureSectionExpanded, setStructureSectionExpanded] = useState(true);
  // Frozen rec order: set once when section first expands for a given post,
  // so items don't jump between pending/actioned groups mid-session.
  const frozenRecOrder = useRef<{ pendingIdxs: number[]; actionedIdxs: number[] } | null>(null);
  const frozenRecOrderPost = useRef<string | null>(null);
  const [copiedHeadingIdx, setCopiedHeadingIdx] = useState<number | null>(null);

  // Fetch recommendations
  const fetchRecommendations = useCallback(async () => {
    if (!currentSite?.id) {
      console.log('No currentSite.id, skipping fetch');
      return;
    }

    console.log('Fetching recommendations for site:', currentSite.id, 'postId filter:', postIdFilter);
    setLoading(true);
    setError(null);

    try {
      const [recsResponse, pairsResponse] = await Promise.all([
        linksApi.getRecommendations(currentSite.id, {
          status: statusFilter !== 'all' ? statusFilter : undefined,
          categoryPair: categoryFilter !== 'all' ? categoryFilter : undefined,
          sortBy,
          sortOrder,
          postId: postIdFilter || undefined,  // Pass postId to backend for server-side filtering
        }),
        linksApi.getCategoryPairs(currentSite.id)
      ]);

      console.log('Recommendations response:', recsResponse);

      if (recsResponse.success) {
        setRecommendations(recsResponse.recommendations || []);
        setStats(recsResponse.stats);
      } else {
        console.error('Recommendations response not successful:', recsResponse);
        setError('Failed to load link recommendations');
      }

      if (pairsResponse.success) {
        setCategoryPairs(pairsResponse.categoryPairs || []);
      }
    } catch (err: unknown) {
      console.error('Failed to fetch recommendations:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load link recommendations: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  }, [currentSite?.id, statusFilter, categoryFilter, sortBy, sortOrder, postIdFilter]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  // Recommendations are now filtered server-side when postIdFilter is passed to the API
  // No client-side filtering needed
  const filteredRecommendations = recommendations;

  // Clear post filter
  const clearPostFilter = () => {
    searchParams.delete('postId');
    searchParams.delete('postTitle');
    setSearchParams(searchParams);
    setPostTitleInput('');
    setTitleOptions([]);
    setShowTitleDropdown(false);
  };

  // Toggle selection
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select all pending
  const selectAll = () => {
    const pendingIds = recommendations.filter(r => r.status === 'pending').map(r => r.id);
    if (selectedIds.size === pendingIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingIds));
    }
  };

  // Handle mark as done
  const handleMarkDone = async (rec: LinkRecommendation) => {
    if (!currentSite?.id || rowActionPending[rec.id]) return;
    setRowActionPending(prev => ({ ...prev, [rec.id]: 'done' }));
    try {
      await linksApi.markApplied(currentSite.id, rec.id);
      // Flash "Applied ✓" for 1.5s then update status
      setRowActionFlash(prev => ({ ...prev, [rec.id]: 'done' }));
      setTimeout(() => {
        setRowActionFlash(prev => { const n = { ...prev }; delete n[rec.id]; return n; });
        setRecommendations(prev => prev.map(r => r.id === rec.id ? { ...r, status: 'applied' } : r));
        setStats(prev => prev ? { ...prev, pending: Math.max(0, prev.pending - 1), applied: prev.applied + 1 } : prev);
        setSelectedIds(prev => { const n = new Set(prev); n.delete(rec.id); return n; });
      }, 1500);
    } catch (err) {
      console.error('Failed to mark as done:', err);
    } finally {
      setRowActionPending(prev => { const n = { ...prev }; delete n[rec.id]; return n; });
    }
  };

  // Handle dismiss
  const handleDismiss = async (rec: LinkRecommendation) => {
    if (!currentSite?.id || rowActionPending[rec.id]) return;
    setRowActionPending(prev => ({ ...prev, [rec.id]: 'dismiss' }));
    try {
      await linksApi.dismiss(currentSite.id, rec.id);
      // Flash "Dismissed" for 1.5s then update status
      setRowActionFlash(prev => ({ ...prev, [rec.id]: 'dismiss' }));
      setTimeout(() => {
        setRowActionFlash(prev => { const n = { ...prev }; delete n[rec.id]; return n; });
        setRecommendations(prev => prev.map(r => r.id === rec.id ? { ...r, status: 'dismissed' } : r));
        setStats(prev => prev ? { ...prev, pending: Math.max(0, prev.pending - 1), dismissed: prev.dismissed + 1 } : prev);
        setSelectedIds(prev => { const n = new Set(prev); n.delete(rec.id); return n; });
      }, 1500);
    } catch (err) {
      console.error('Failed to dismiss:', err);
    } finally {
      setRowActionPending(prev => { const n = { ...prev }; delete n[rec.id]; return n; });
    }
  };

  // Handle revert to pending (undo applied or dismissed)
  const handleSetToPending = async (rec: LinkRecommendation) => {
    if (!currentSite?.id || rowActionPending[rec.id]) return;
    setRowActionPending(prev => ({ ...prev, [rec.id]: 'done' }));
    try {
      await linksApi.setToPending(currentSite.id, rec.id);
      setRecommendations(prev => prev.map(r => r.id === rec.id ? { ...r, status: 'pending', linkAppliedDate: undefined } : r));
      setStats(prev => {
        if (!prev) return prev;
        const fromApplied = rec.status === 'applied';
        return {
          ...prev,
          pending: prev.pending + 1,
          applied: fromApplied ? Math.max(0, prev.applied - 1) : prev.applied,
          dismissed: !fromApplied ? Math.max(0, prev.dismissed - 1) : prev.dismissed,
        };
      });
    } catch (err) {
      console.error('Failed to revert to pending:', err);
    } finally {
      setRowActionPending(prev => { const n = { ...prev }; delete n[rec.id]; return n; });
    }
  };

  // Bulk mark as implemented
  const handleBulkMarkDone = async () => {
    if (!currentSite?.id || selectedIds.size === 0) return;

    for (const id of selectedIds) {
      const rec = recommendations.find(r => r.id === id);
      if (rec) {
        await handleMarkDone(rec);
      }
    }
  };

  // Bulk dismiss
  const handleBulkDismiss = async () => {
    if (!currentSite?.id || selectedIds.size === 0) return;

    for (const id of selectedIds) {
      const rec = recommendations.find(r => r.id === id);
      if (rec) {
        await handleDismiss(rec);
      }
    }
  };

  // Handle generate semantic link (direct BKA call from frontend)
  const handleGenerateSemanticLink = async (rec: LinkRecommendation) => {
    if (!currentSite?.id || !isPro) return;

    setSemanticModal({
      show: true,
      loading: true,
      recommendation: rec,
      error: null,
      structureAnalysis: null,
      structureAnalysisFailed: false,
      categoryRecommendation: null,
      structureState: null,
      sourcePostId: null,
      contentMd5: null,
      enableStructureAnalysis: true,
      enableCategoryRecommendation: true,
    });

    try {
      // Step 1: Get prepared data from backend (credentials + request data)
      console.log('Preparing semantic link data...');
      const prepareResponse = await linksApi.prepareSemanticLink(currentSite.id, rec.id);
      
      if (!prepareResponse.success) {
        throw new Error(prepareResponse.error || 'Failed to prepare semantic link data');
      }

      // If already generated, just show it (but load structure state too).
      // Override targetPostUrl from the prepare response so the UI always has a real URL.
      if (prepareResponse.alreadyGenerated) {
        const recWithUrl: LinkRecommendation = {
          ...rec,
          targetPostUrl: prepareResponse.targetUrl || rec.targetPostUrl || '',
        };
        handleViewSemanticLink(recWithUrl, {
          structureAnalysis: prepareResponse.cachedStructureAnalysis ?? null,
          categoryRecommendation: prepareResponse.cachedCategoryRecommendation ?? null,
          structureState: prepareResponse.structureState ?? null,
          sourcePostId: String(rec.sourcePostId),
          contentMd5: prepareResponse.contentMd5 ?? null,
          enableStructureAnalysis: prepareResponse.enableStructureAnalysis ?? true,
          enableCategoryRecommendation: prepareResponse.enableCategoryRecommendation ?? true,
        });
        return;
      }

      if (!prepareResponse.credentials || !prepareResponse.bkaUrl || !prepareResponse.requestData) {
        throw new Error('Missing required data for BKA call');
      }

      // include_structure is passed as a query param in bkaUrl — do not duplicate in body
      const bkaBody = prepareResponse.requestData;

      // Step 2: Call BKA API directly from frontend
      console.log('Calling BKA API directly:', prepareResponse.bkaUrl, '| include_structure:', prepareResponse.includeStructure);
      const bkaResponse = await fetch(prepareResponse.bkaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-License-Key': prepareResponse.credentials.licenseKey,
          'X-Domain': prepareResponse.credentials.domain,
          'X-Instance-Key': prepareResponse.credentials.instanceKey,
          'X-API-Key': prepareResponse.credentials.claudeApiKey
        },
        body: JSON.stringify(bkaBody)
      });

      if (!bkaResponse.ok) {
        const errorData = await bkaResponse.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `BKA API error: ${bkaResponse.status}`);
      }

      const bkaResult = await bkaResponse.json();
      // Log full BKA response to help diagnose structure_analysis response path
      console.log('BKA API response top-level keys:', Object.keys(bkaResult));
      console.log('BKA API response:', bkaResult);

      if (!bkaResult.success) {
        throw new Error(bkaResult.message || 'BKA API returned unsuccessful response');
      }

      // Step 3: Save the result to backend (pass contentMd5 + sourcePostId for structure upsert)
      console.log('Saving semantic link result...');
      const sourcePostId = String(rec.sourcePostId);
      const saveResponse = await linksApi.saveSemanticResult(
        rec.id,
        currentSite.id,
        bkaResult,
        prepareResponse.contentMd5,
        sourcePostId
      );
      
      if (!saveResponse.success) {
        console.warn('Failed to save result, but BKA call succeeded:', saveResponse.error);
      }

      // WP plugin primary path: data.data.*  fallback: data.*
      // (BKA may wrap fields one level deeper in some responses)
      const innerData = bkaResult.data?.data || bkaResult.data;
      const recommendation = innerData?.recommendation || innerData;

      // Match WP plugin extraction order: data.data first, then data, then top-level
      const freshStructureAnalysis: StructureAnalysis | null =
        bkaResult.data?.data?.structure_analysis ??
        bkaResult.data?.structure_analysis ??
        bkaResult.structure_analysis ??
        null;
      const freshCategoryRecommendation: PrimaryTagRecommendation | null =
        bkaResult.data?.data?.primary_category_recommendation ??
        bkaResult.data?.primary_category_recommendation ??
        bkaResult.primary_category_recommendation ??
        null;
      // BKA signals structure analysis failure with structure_analysis_failed: true
      const freshStructureAnalysisFailed: boolean =
        bkaResult.data?.data?.structure_analysis_failed === true ||
        bkaResult.data?.structure_analysis_failed === true ||
        bkaResult.structure_analysis_failed === true ||
        saveResponse.structureAnalysisFailed === true;
      console.log('freshStructureAnalysis found:', !!freshStructureAnalysis,
        '| freshCategoryRecommendation found:', !!freshCategoryRecommendation,
        '| structureAnalysisFailed:', freshStructureAnalysisFailed);

      // Build fresh structure state (new analysis resets per-post state)
      const freshStructureState: StructureState | null = freshStructureAnalysis
        ? {
            score: freshStructureAnalysis.analysis_summary?.overall_score ?? null,
            analysisDate: new Date().toISOString(),
            completedAt: null,
            allSkipped: false,
            recommendationsApplied: [],
            recommendationsSkipped: [],
            recommendationsManual: [],
            undoData: {},
            categoryAcknowledged: null,
          }
        : (prepareResponse.structureState ?? null);
      
      const optimalPlacement = recommendation?.recommended_placement || recommendation?.optimal_placement || undefined;
      const matchAnchor = recommendation?.match_anchor || recommendation?.anchor_text || undefined;
      const currentParagraph = recommendation?.current_paragraph || undefined;
      // BKA field priority: recommended_replacement > replacement_paragraph > replacement_html
      const recommendedReplacement = recommendation?.recommended_replacement || recommendation?.replacement_paragraph || recommendation?.replacement_html || undefined;
      const placementRationale = recommendation?.explanation || recommendation?.placement_rationale || undefined;
      const confidence = recommendation?.confidence ?? undefined;
      // Use the URL from the prepare response (pre-resolved by the backend) with fallbacks
      const targetPostUrl = prepareResponse.requestData?.to_post?.url || '';
      
      setRecommendations(prev => 
        prev.map(r => r.id === rec.id ? {
          ...r,
          semanticLinkRequested: true,
          optimalPlacement,
          matchAnchor,
          currentParagraph,
          recommendedReplacement,
          placementRationale,
          confidence,
          targetPostUrl
        } : r)
      );

      const isStructureComplete = !!(freshStructureState?.completedAt);
      // Collapse heading recs by default when already complete; expand when there's work to do.
      setStructureSectionExpanded(!isStructureComplete);
      setSemanticModal({
        show: true,
        loading: false,
        recommendation: {
          ...rec,
          optimalPlacement,
          matchAnchor,
          currentParagraph,
          recommendedReplacement,
          placementRationale,
          confidence,
          targetPostUrl
        },
        error: null,
        structureAnalysis: freshStructureAnalysis ?? prepareResponse.cachedStructureAnalysis ?? null,
        structureAnalysisFailed: freshStructureAnalysisFailed,
        categoryRecommendation: freshCategoryRecommendation ?? prepareResponse.cachedCategoryRecommendation ?? null,
        structureState: freshStructureState,
        sourcePostId,
        contentMd5: prepareResponse.contentMd5 ?? null,
        enableStructureAnalysis: prepareResponse.enableStructureAnalysis ?? true,
        enableCategoryRecommendation: prepareResponse.enableCategoryRecommendation ?? true,
      });
    } catch (err) {
      const error = err as Error;
      console.error('Semantic link generation error:', error);
      setSemanticModal(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  // View existing semantic link (optionally pre-populate structure data)
  const handleViewSemanticLink = (
    rec: LinkRecommendation,
    structureData?: {
      structureAnalysis: StructureAnalysis | null;
      structureAnalysisFailed?: boolean;
      categoryRecommendation: PrimaryTagRecommendation | null;
      structureState: StructureState | null;
      sourcePostId: string;
      contentMd5: string | null;
      enableStructureAnalysis: boolean;
      enableCategoryRecommendation: boolean;
    }
  ) => {
    // Collapse heading recs by default when already complete; expand when there's work to do.
    setStructureSectionExpanded(!(structureData?.structureState?.completedAt));
    setSemanticModal({
      show: true,
      loading: false,
      recommendation: rec,
      error: null,
      structureAnalysis: structureData?.structureAnalysis ?? null,
      structureAnalysisFailed: structureData?.structureAnalysisFailed ?? false,
      categoryRecommendation: structureData?.categoryRecommendation ?? null,
      structureState: structureData?.structureState ?? null,
      sourcePostId: structureData?.sourcePostId ?? String(rec.sourcePostId),
      contentMd5: structureData?.contentMd5 ?? null,
      enableStructureAnalysis: structureData?.enableStructureAnalysis ?? true,
      enableCategoryRecommendation: structureData?.enableCategoryRecommendation ?? true,
    });
  };

  // ── Heading structure action handlers ────────────────────────────────────

  const refreshHeadingActionVersion = () => setHeadingActionVersion(v => v + 1);

  // Heading apply/undo refusals come back as 409/422 with a user-facing `error`.
  const headingActionErrorMessage = (err: unknown, fallback: string): string => {
    const axiosErr = err as { response?: { data?: { error?: string } } };
    return axiosErr?.response?.data?.error || fallback;
  };

  const handleApplyHeading = async (recIndex: number) => {
    if (!currentSite?.id || !semanticModal.sourcePostId) return;
    if (pendingHeadingActions.current.has(recIndex)) return;
    pendingHeadingActions.current.add(recIndex);
    // Clear any previous error for this rec
    setHeadingApplyErrors(prev => { const n = { ...prev }; delete n[recIndex]; return n; });
    refreshHeadingActionVersion();
    try {
      const result = await structureApi.applyHeading(
        currentSite.id,
        semanticModal.sourcePostId,
        recIndex
      );
      if (result.success) {
        setSemanticModal(prev => {
          const st = prev.structureState;
          if (!st) return prev;
          const applied = [...st.recommendationsApplied.filter(i => i !== recIndex), recIndex];
          const undoData = { ...st.undoData, [String(recIndex)]: result.undoData };
          return {
            ...prev,
            structureState: {
              ...st,
              recommendationsApplied: applied,
              recommendationsSkipped: st.recommendationsSkipped.filter(i => i !== recIndex),
              recommendationsManual: st.recommendationsManual.filter(i => i !== recIndex),
              undoData,
              completedAt: result.autoCompleted ? new Date().toISOString() : st.completedAt,
            },
          };
        });
      } else {
        const msg = result.error || 'Could not apply — the heading text was not found in the post. Use "Applied Manually" after editing the post directly.';
        console.error('applyHeading failed:', msg);
        setHeadingApplyErrors(prev => ({ ...prev, [recIndex]: msg }));
      }
    } catch (err) {
      console.error('applyHeading error:', err);
      setHeadingApplyErrors(prev => ({ ...prev, [recIndex]: headingActionErrorMessage(err, 'Request failed — please try again.') }));
    } finally {
      pendingHeadingActions.current.delete(recIndex);
      refreshHeadingActionVersion();
    }
  };

  const handleSkipHeading = async (recIndex: number) => {
    if (!currentSite?.id || !semanticModal.sourcePostId) return;
    if (pendingHeadingActions.current.has(recIndex)) return;
    pendingHeadingActions.current.add(recIndex);
    refreshHeadingActionVersion();
    try {
      const result = await structureApi.skipHeading(
        currentSite.id,
        semanticModal.sourcePostId,
        recIndex
      );
      if (result.success) {
        setSemanticModal(prev => {
          const st = prev.structureState;
          if (!st) return prev;
          const skipped = [...st.recommendationsSkipped.filter(i => i !== recIndex), recIndex];
          return {
            ...prev,
            structureState: {
              ...st,
              recommendationsApplied: st.recommendationsApplied.filter(i => i !== recIndex),
              recommendationsSkipped: skipped,
              recommendationsManual: st.recommendationsManual.filter(i => i !== recIndex),
              completedAt: result.autoCompleted ? new Date().toISOString() : st.completedAt,
            },
          };
        });
      }
    } catch (err) {
      console.error('skipHeading error:', err);
    } finally {
      pendingHeadingActions.current.delete(recIndex);
      refreshHeadingActionVersion();
    }
  };

  const handleManualHeading = async (recIndex: number) => {
    if (!currentSite?.id || !semanticModal.sourcePostId) return;
    if (pendingHeadingActions.current.has(recIndex)) return;
    pendingHeadingActions.current.add(recIndex);
    refreshHeadingActionVersion();
    try {
      const result = await structureApi.manualHeading(
        currentSite.id,
        semanticModal.sourcePostId,
        recIndex
      );
      if (result.success) {
        setSemanticModal(prev => {
          const st = prev.structureState;
          if (!st) return prev;
          const manual = [...st.recommendationsManual.filter(i => i !== recIndex), recIndex];
          return {
            ...prev,
            structureState: {
              ...st,
              recommendationsApplied: st.recommendationsApplied.filter(i => i !== recIndex),
              recommendationsSkipped: st.recommendationsSkipped.filter(i => i !== recIndex),
              recommendationsManual: manual,
              completedAt: result.autoCompleted ? new Date().toISOString() : st.completedAt,
            },
          };
        });
      }
    } catch (err) {
      console.error('manualHeading error:', err);
    } finally {
      pendingHeadingActions.current.delete(recIndex);
      refreshHeadingActionVersion();
    }
  };

  const handleUndoHeading = async (recIndex: number) => {
    if (!currentSite?.id || !semanticModal.sourcePostId) return;
    if (pendingHeadingActions.current.has(recIndex)) return;
    pendingHeadingActions.current.add(recIndex);
    setHeadingApplyErrors(prev => { const n = { ...prev }; delete n[recIndex]; return n; });
    refreshHeadingActionVersion();
    try {
      const result = await structureApi.undoHeading(
        currentSite.id,
        semanticModal.sourcePostId,
        recIndex
      );
      if (result.success) {
        setSemanticModal(prev => {
          const st = prev.structureState;
          if (!st) return prev;
          const undoData = { ...st.undoData };
          delete undoData[String(recIndex)];
          return {
            ...prev,
            structureState: {
              ...st,
              recommendationsApplied: st.recommendationsApplied.filter(i => i !== recIndex),
              undoData,
              completedAt: null,
            },
          };
        });
      }
    } catch (err) {
      console.error('undoHeading error:', err);
      setHeadingApplyErrors(prev => ({ ...prev, [recIndex]: headingActionErrorMessage(err, 'Undo failed — please try again.') }));
    } finally {
      pendingHeadingActions.current.delete(recIndex);
      refreshHeadingActionVersion();
    }
  };

  const handleMarkComplete = async () => {
    if (!currentSite?.id || !semanticModal.sourcePostId) return;
    try {
      const result = await structureApi.markComplete(currentSite.id, semanticModal.sourcePostId);
      if (result.success) {
        setSemanticModal(prev => ({
          ...prev,
          structureState: prev.structureState
            ? { ...prev.structureState, completedAt: new Date().toISOString() }
            : prev.structureState,
        }));
      }
    } catch (err) {
      console.error('markComplete error:', err);
    }
  };

  const handleSkipAll = async () => {
    if (!currentSite?.id || !semanticModal.sourcePostId || !semanticModal.structureAnalysis) return;
    try {
      const result = await structureApi.skipAll(currentSite.id, semanticModal.sourcePostId);
      if (result.success) {
        const allIndices = semanticModal.structureAnalysis.recommendations
          .map((_, i) => i)
          .filter(i => {
            const st = semanticModal.structureState;
            if (!st) return true;
            return !st.recommendationsApplied.includes(i) &&
              !st.recommendationsSkipped.includes(i) &&
              !st.recommendationsManual.includes(i);
          });
        setSemanticModal(prev => ({
          ...prev,
          structureState: prev.structureState
            ? {
                ...prev.structureState,
                recommendationsSkipped: [
                  ...prev.structureState.recommendationsSkipped,
                  ...allIndices,
                ],
                completedAt: new Date().toISOString(),
                allSkipped: true,
              }
            : prev.structureState,
        }));
      }
    } catch (err) {
      console.error('skipAll error:', err);
    }
  };

  const handleReanalyze = async () => {
    if (!currentSite?.id || !semanticModal.sourcePostId) return;
    try {
      await structureApi.requestReanalyze(currentSite.id, semanticModal.sourcePostId);
      setSemanticModal(prev => ({
        ...prev,
        structureState: prev.structureState
          ? { ...prev.structureState, completedAt: null }
          : prev.structureState,
      }));
    } catch (err) {
      console.error('requestReanalyze error:', err);
    }
  };

  const handleAckCategory = async (action: 'apply' | 'dismiss') => {
    if (!currentSite?.id || !semanticModal.sourcePostId || !semanticModal.categoryRecommendation) return;
    setCategoryApplying(true);
    setShowCategoryConfirm(false);
    try {
      const catRec = semanticModal.categoryRecommendation;
      // Resolve the recommended primary tag name from BKA field names (with fallback)
      const primaryTagName = catRec.recommended_primary || catRec.primary_tag || '';
      const result = await structureApi.ackCategory(
        currentSite.id,
        semanticModal.sourcePostId,
        {
          action,
          primaryTagName: action === 'apply' ? primaryTagName : undefined,
        }
      );
      if (result.success) {
        setSemanticModal(prev => ({
          ...prev,
          structureState: prev.structureState
            ? { ...prev.structureState, categoryAcknowledged: true }
            : prev.structureState,
        }));
      }
    } catch (err) {
      console.error('ackCategory error:', err);
    } finally {
      setCategoryApplying(false);
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Get status badge color
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'applied':
        return 'bg-[#22c55e]/20 text-[#22c55e]';
      case 'dismissed':
        return 'bg-[var(--text-muted)]/20 text-white/70';
      case 'existing':
      case 'verified':
        return 'bg-[#3b82f6]/20 text-[#3b82f6]';
      default:
        return 'bg-[#f59e0b]/20 text-[#f59e0b]';
    }
  };

  // Get priority score color
  // Score badge uses VizzEx yellow with varying opacity based on score
  const getPriorityScoreClass = (score: number) => {
    if (score >= 0.7) return 'bg-[#ffdc18] text-black';
    if (score >= 0.4) return 'bg-[#ffdc18]/70 text-black';
    return 'bg-[#ffdc18]/40 text-black';
  };

  // No site
  if (!currentSite) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold">Link Recommendations</h1>
          <p className="text-[var(--text-secondary)] mt-1">No site connected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Link Recommendations</h1>
      </div>

      {/* Post Filter Banner */}
      {postIdFilter && (
        <div className="space-y-3">
          <div className="p-4 bg-[#ffdc18]/10 border border-[#ffdc18]/30 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#ffdc18]" />
              <span className="font-medium">
                Filtered by Post: <span className="text-[#ffdc18]">
                  {(() => {
                    // First try to get title from URL params (passed from Dashboard)
                    const postTitleFromUrl = searchParams.get('postTitle');
                    if (postTitleFromUrl) {
                      return `${postTitleFromUrl} (${postIdFilter})`;
                    }
                    
                    // Fallback: Find the post title from recommendations
                    const postId = parseInt(postIdFilter, 10);
                    const matchingRec = filteredRecommendations.find(
                      r => r.sourcePostId === postId || r.targetPostId === postId
                    );
                    if (matchingRec) {
                      const title = matchingRec.sourcePostId === postId 
                        ? matchingRec.sourcePostTitle 
                        : matchingRec.targetPostTitle;
                      if (title) {
                        return `${title} (${postIdFilter})`;
                      }
                    }
                    // Show loading or ID as fallback
                    return loading ? 'Loading...' : `Post ID: ${postIdFilter}`;
                  })()}
                </span>
              </span>
            </div>
            <button
              onClick={clearPostFilter}
              className="px-3 py-1.5 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-secondary)] rounded text-sm font-medium transition-colors flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Clear Filter
            </button>
          </div>
        </div>
      )}

      {/* Overview Stats */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Overview</h2>
        <div className="grid grid-cols-5 gap-3">
          <div className="p-4 bg-white/5 border border-[#3b82f6]/30 rounded-lg text-center">
            <p className="text-3xl font-bold text-[#3b82f6]">{stats?.pending || 0}</p>
            <p className="text-sm text-white/70 uppercase tracking-wide">Pending</p>
          </div>
          <div className="p-4 bg-white/5 border border-[#22c55e]/30 rounded-lg text-center">
            <p className="text-3xl font-bold text-[#22c55e]">{stats?.applied || 0}</p>
            <p className="text-sm text-white/70 uppercase tracking-wide">Implemented</p>
          </div>
          <div className="p-4 bg-white/5 border border-[var(--text-muted)]/30 rounded-lg text-center">
            <p className="text-3xl font-bold text-white/70">{stats?.dismissed || 0}</p>
            <p className="text-sm text-white/70 uppercase tracking-wide">Dismissed</p>
          </div>
          <div className="p-4 bg-white/5 border border-[#8b5cf6]/30 rounded-lg text-center">
            <p className="text-3xl font-bold text-[#8b5cf6]">{stats?.verified || 0}</p>
            <p className="text-sm text-white/70 uppercase tracking-wide">Verified</p>
          </div>
          <div className="p-4 bg-white/5 border border-[#06b6d4]/30 rounded-lg text-center">
            <p className="text-3xl font-bold text-[#06b6d4]">{stats?.total || 0}</p>
            <p className="text-sm text-white/70 uppercase tracking-wide">Total</p>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="glass rounded-xl p-4 space-y-4">
        {/* Post Title Search — typeahead that switches to the postId-filtered view on selection */}
        <div>
          <label className="text-sm font-medium block mb-1">Filter by Post Title:</label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <input
                type="text"
                value={postTitleInput}
                onChange={(e) => setPostTitleInput(e.target.value)}
                onFocus={() => { if (titleOptions.length > 0) setShowTitleDropdown(true); }}
                onBlur={() => { setTimeout(() => setShowTitleDropdown(false), 150); }}
                placeholder="Search posts with recommendations…"
                className="w-full px-3 py-2 pr-8 bg-[var(--bg-tertiary)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffdc18]"
              />
              {postTitleInput && (
                <button
                  type="button"
                  onClick={clearPostFilter}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80"
                  aria-label="Clear post title search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {showTitleDropdown && titleOptions.length > 0 && (
                <div className="absolute z-30 mt-1 w-full min-w-[320px] bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] rounded-lg shadow-xl overflow-hidden">
                  {titleOptions.map(opt => (
                    <button
                      key={opt.postId}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectTitleOption(opt)}
                      className="w-full text-left px-4 py-2.5 hover:bg-[var(--bg-tertiary)] flex items-center justify-between gap-3 border-b border-[var(--bg-tertiary)] last:border-0 transition-colors"
                    >
                      <span className="text-sm text-white/90 truncate">{opt.title}</span>
                      <span className="text-xs text-white/40 whitespace-nowrap">({opt.linkCount} link{opt.linkCount !== 1 ? 's' : ''})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {postIdFilter && (
              <button
                type="button"
                onClick={clearPostFilter}
                className="px-3 py-2 text-sm font-medium border border-[#ffdc18]/40 text-[#ffdc18] rounded-lg hover:bg-[#ffdc18]/10 transition-colors whitespace-nowrap"
              >
                All posts
              </button>
            )}
          </div>
          <p className="text-xs text-white/40 mt-1">
            Limits the list to recommendations involving the selected post (From or To). Other filters apply within that set. Choose "All posts" to remove this limit.
          </p>
        </div>

        <div className="flex flex-wrap gap-6 items-end">
          {/* Status Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Filter by Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-[var(--bg-tertiary)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffdc18] min-w-[150px]"
            >
              <option value="pending">Pending Only</option>
              <option value="applied">Implemented Only</option>
              <option value="dismissed">Dismissed Only</option>
              <option value="existing">Verified Only</option>
              <option value="all">Show All</option>
            </select>
          </div>

          {/* Category Pair Filter */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Filter by Category Pair:</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 bg-[var(--bg-tertiary)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffdc18] min-w-[200px]"
            >
              <option value="all">All Category Pairs</option>
              {categoryPairs.map(pair => (
                <option key={pair} value={pair}>{pair}</option>
              ))}
            </select>
          </div>

          {/* Sort By */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 bg-[var(--bg-tertiary)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffdc18] min-w-[150px]"
            >
              <option value="priority_score">Strategic Score</option>
              <option value="impact_points">Max Impact</option>
              <option value="source_impact">From Post Impact</option>
              <option value="target_impact">To Post Impact</option>
              <option value="created_at">Date Recommended</option>
              <option value="relationship_type">Relationship Type</option>
              <option value="source_post_title">From Title</option>
              <option value="target_post_title">To Title</option>
            </select>
          </div>

          {/* Sort Order */}
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Order:</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              className="px-3 py-2 bg-[var(--bg-tertiary)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ffdc18]"
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions - Only for pending */}
        {statusFilter === 'pending' && (
          <div className="mt-4 pt-4 border-t border-[var(--bg-tertiary)] flex items-center gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.size > 0 && selectedIds.size === recommendations.filter(r => r.status === 'pending').length}
                onChange={selectAll}
                className="w-4 h-4 rounded accent-[#ffdc18]"
              />
              <span className="text-sm">Select All</span>
            </label>
            
            <button
              onClick={handleBulkMarkDone}
              disabled={selectedIds.size === 0}
              className="px-3 py-1.5 bg-[var(--bg-tertiary)] rounded text-sm hover:bg-[var(--bg-tertiary)]/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Check className="w-4 h-4" />
              Mark as Implemented
            </button>
            
            <button
              onClick={handleBulkDismiss}
              disabled={selectedIds.size === 0}
              className="px-3 py-1.5 bg-[var(--bg-tertiary)] rounded text-sm hover:bg-[var(--bg-tertiary)]/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              Dismiss Selected
            </button>
          </div>
        )}
        
        {/* Note - full width, left justified */}
        <div className="p-3 bg-[#ffdc18]/10 border border-[#ffdc18]/30 rounded-lg text-sm text-white/80">
          💡 <strong>Note:</strong> Links are not automatically added to your posts. Use the action buttons to track which recommendations you have manually implemented in your content.
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-[var(--color-error)]" />
          <span className="text-[var(--color-error)]">{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#ffdc18] animate-spin" />
        </div>
      )}

      {/* No Recommendations */}
      {!loading && filteredRecommendations.length === 0 && (
        <div className="glass rounded-xl p-8 text-center">
          <Link2 className="w-12 h-12 text-white/70 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Recommendations</h3>
          <p className="text-[var(--text-secondary)]">
            Run an analysis to generate link recommendations.
          </p>
        </div>
      )}

      {/* Recommendations Table */}
      {!loading && filteredRecommendations.length > 0 && (
        <div className="glass rounded-xl overflow-hidden">
          {/* Direction Key - only shown when filtered by postId */}
          {/* Direction Key - only show when filtered by postId */}
          {postIdFilter && (
            <div className="flex items-center gap-6 p-4 border-b border-[var(--bg-tertiary)] bg-[var(--bg-secondary)]">
              <span className="font-semibold text-white">Direction Key:</span>
              <span className="flex items-center gap-2">
                <span 
                  className="w-3 h-6 rounded-sm"
                  style={{ backgroundColor: '#3b82f6' }}
                ></span>
                <span className="text-[#60a5fa] font-medium">Inbound</span>
                <span className="text-white/60">(links TO this post)</span>
              </span>
              <span className="flex items-center gap-2">
                <span 
                  className="w-3 h-6 rounded-sm"
                  style={{ backgroundColor: '#22c55a' }}
                ></span>
                <span className="text-[#4ade80] font-medium">Outbound</span>
                <span className="text-white/60">(links FROM this post)</span>
              </span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="text-sm min-w-[1700px]">
              <thead className="bg-[var(--bg-tertiary)]">
                <tr>
                  {/* Direction indicator column when filtered by postId, otherwise checkbox for bulk actions */}
                  {postIdFilter ? (
                    <th 
                      className="sticky left-0 bg-[var(--bg-tertiary)] z-20"
                      style={{ width: '8px', minWidth: '8px', padding: 0 }}
                    >&nbsp;</th>
                  ) : statusFilter === 'pending' ? (
                    <th className="p-3 text-left w-10 sticky left-0 bg-[var(--bg-tertiary)] z-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.size > 0 && selectedIds.size === recommendations.filter(r => r.status === 'pending').length}
                        onChange={selectAll}
                        className="w-4 h-4 rounded accent-[#ffdc18]"
                      />
                    </th>
                  ) : null}
                  <th className="p-3 text-left text-xs whitespace-nowrap min-w-[80px]">
                    {statusFilter === 'applied' || statusFilter === 'existing' ? 'Applied' : 'Recommended'}
                  </th>
                  <th className="p-3 text-center text-xs whitespace-nowrap min-w-[60px]">Score</th>
                  {/* Impact columns - single when filtered, dual when not */}
                  {postIdFilter ? (
                    <th className="p-3 text-center text-xs whitespace-nowrap min-w-[80px]">Impact</th>
                  ) : (
                    <>
                      <th className="p-3 text-center text-xs whitespace-nowrap min-w-[80px]">From Impact</th>
                      <th className="p-3 text-center text-xs whitespace-nowrap min-w-[80px]">To Impact</th>
                    </>
                  )}
                  <th className="p-3 text-left text-xs whitespace-nowrap min-w-[130px]">Relationship</th>
                  <th className="p-3 text-left text-xs whitespace-nowrap min-w-[250px]">From Post</th>
                  <th className="p-3 text-left text-xs whitespace-nowrap min-w-[250px]">To Post</th>
                  <th className="p-3 text-left text-xs whitespace-nowrap min-w-[150px]">Anchor Text</th>
                  <th className="p-3 text-center text-xs whitespace-nowrap min-w-[80px]">Status</th>
                  <th className="p-3 text-right text-xs whitespace-nowrap min-w-[280px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecommendations.map((rec) => {
                  const normalizedType = normalizeRelationshipType(rec.relationshipType || '');
                  const relDef = relationshipDefs[normalizedType];
                  const isRelExpanded = expandedRelRows.has(rec.id);
                  
                  // Determine direction when filtered by postId
                  // Convert all to strings for reliable comparison
                  const postId = postIdFilter || null;
                  const isInbound = postId !== null && String(rec.targetPostId) === String(postId);
                  const isOutbound = postId !== null && String(rec.sourcePostId) === String(postId);
                  
                  return (
                    <React.Fragment key={rec.id}>
                      <tr className="border-b border-[var(--bg-tertiary)]">
                        {/* Direction indicator (solid color) when filtered by postId, otherwise checkbox for bulk actions */}
                        {postIdFilter ? (
                          <td 
                            className="sticky left-0 z-20"
                            style={{ 
                              backgroundColor: isInbound ? '#3b82f6' : isOutbound ? '#22c55a' : '#6b7280',
                              width: '8px',
                              minWidth: '8px',
                              maxWidth: '8px',
                              padding: 0
                            }}
                          ></td>
                        ) : statusFilter === 'pending' ? (
                          <td className="p-3 sticky left-0 z-10 bg-[var(--bg-primary)]">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(rec.id)}
                              onChange={() => toggleSelect(rec.id)}
                              className="w-4 h-4 rounded accent-[#ffdc18]"
                            />
                          </td>
                        ) : null}
                        
                        {/* Date - show linkAppliedDate for applied/existing, createdAt otherwise */}
                        <td className="p-3 whitespace-nowrap text-xs">
                          {(rec.status === 'applied' || rec.status === 'existing') && rec.linkAppliedDate
                            ? formatDate(rec.linkAppliedDate)
                            : formatDate(rec.createdAt)}
                        </td>
                        
                        {/* Score - priorityScore is 0-1, display as 0-10 */}
                        <td className="p-3 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded font-bold text-xs ${getPriorityScoreClass(rec.priorityScore || 0)}`}>
                            {Math.round((rec.priorityScore || 0) * 10)}
                          </span>
                        </td>
                        
                        {/* Impact cells - single when filtered, dual when not */}
                        {postIdFilter ? (
                          <td className="p-3 text-center">
                            {(() => {
                              const pId = parseInt(postIdFilter, 10);
                              const inbound = rec.targetPostId === pId;
                              const impact = inbound ? rec.targetPostImpact : rec.sourcePostImpact;
                              if (!impact) return <span className="text-white/50">—</span>;
                              return (
                                <div className="flex flex-col items-center">
                                  <span className={inbound ? 'text-[#60a5fa] font-semibold text-xs' : 'text-[#4ade80] font-semibold text-xs'}>
                                    +{impact.points?.toFixed(1) || '0.0'}
                                  </span>
                                  {impact.will_tier_up && (
                                    <span className="text-[10px] text-[#fbbf24]">⭐{impact.new_tier}</span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        ) : (
                          <>
                            <td className="p-3 text-center">
                              {rec.sourcePostImpact ? (
                                <div className="flex flex-col items-center">
                                  <span className="text-[#4ade80] font-semibold text-xs">+{rec.sourcePostImpact.points?.toFixed(1) || '0.0'}</span>
                                  {rec.sourcePostImpact.will_tier_up && (
                                    <span className="text-[10px] text-[#fbbf24]">⭐{rec.sourcePostImpact.new_tier}</span>
                                  )}
                                </div>
                              ) : <span className="text-white/50">—</span>}
                            </td>
                            <td className="p-3 text-center">
                              {rec.targetPostImpact ? (
                                <div className="flex flex-col items-center">
                                  <span className="text-[#60a5fa] font-semibold text-xs">+{rec.targetPostImpact.points?.toFixed(1) || '0.0'}</span>
                                  {rec.targetPostImpact.will_tier_up && (
                                    <span className="text-[10px] text-[#fbbf24]">⭐{rec.targetPostImpact.new_tier}</span>
                                  )}
                                </div>
                              ) : <span className="text-white/50">—</span>}
                            </td>
                          </>
                        )}
                        
                        {/* Relationship Type - CLICKABLE */}
                        <td className="p-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRelationshipInfo(rec.id);
                            }}
                            title={shortDefinitions[normalizedType] || 'Semantic relationship between posts'}
                            className="px-2 py-1 bg-[#ffdc18]/10 text-[#ffdc18] rounded text-xs font-medium hover:bg-[#ffdc18]/20 transition-colors flex items-center gap-1.5 cursor-pointer"
                          >
                            <span>{relDef?.title || rec.relationshipType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '—'}</span>
                            <Info className="w-3 h-3 flex-shrink-0" />
                          </button>
                        </td>
                        
                        {/* From Post with Hub Badge + Edit Link */}
                        <td className="p-3">
                          <div className="flex items-center gap-1.5">
                            {rec.sourceIsHub && (
                              <span 
                                className="px-1.5 py-0.5 rounded text-xs font-medium"
                                style={{ 
                                  backgroundColor: rec.sourceHubStatus === 'under_connected' 
                                    ? 'rgba(255,193,7,0.2)' 
                                    : rec.sourceHubStatus === 'strong' 
                                      ? 'rgba(76,175,80,0.2)' 
                                      : 'rgba(0,188,212,0.2)',
                                  color: rec.sourceHubStatus === 'under_connected' 
                                    ? '#ffc107' 
                                    : rec.sourceHubStatus === 'strong' 
                                      ? '#4caf50' 
                                      : '#00bcd4'
                                }}
                                title={`Hub for "${rec.sourceHubCategory}" - ${rec.sourceHubStatus === 'under_connected' ? 'Needs more connections' : rec.sourceHubStatus === 'strong' ? 'Strong hub' : 'On track'}`}
                              >
                                🏛️
                              </span>
                            )}
                            <span className="font-medium text-xs">{rec.sourcePostTitle}</span>
                            {currentSite?.platformId && (
                              <a
                                href={`https://app.hubspot.com/blog/${currentSite.platformId}/editor/${rec.sourcePostId}/content`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#ffdc18] hover:text-[#ffdc18]/80 transition-colors"
                                title="Edit in HubSpot"
                              >
                                <Pencil className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </td>
                        
                        {/* To Post with Hub Badge + View Link */}
                        <td className="p-3">
                          <div className="flex items-center gap-1.5">
                            {rec.targetIsHub && (
                              <span 
                                className="px-1.5 py-0.5 rounded text-xs font-medium"
                                style={{ 
                                  backgroundColor: rec.targetHubStatus === 'under_connected' 
                                    ? 'rgba(255,193,7,0.2)' 
                                    : rec.targetHubStatus === 'strong' 
                                      ? 'rgba(76,175,80,0.2)' 
                                      : 'rgba(0,188,212,0.2)',
                                  color: rec.targetHubStatus === 'under_connected' 
                                    ? '#ffc107' 
                                    : rec.targetHubStatus === 'strong' 
                                      ? '#4caf50' 
                                      : '#00bcd4'
                                }}
                                title={`Hub for "${rec.targetHubCategory}" - ${rec.targetHubStatus === 'under_connected' ? 'Needs more connections' : rec.targetHubStatus === 'strong' ? 'Strong hub' : 'On track'}`}
                              >
                                🏛️
                              </span>
                            )}
                            <span className="font-medium text-xs">{rec.targetPostTitle}</span>
                            {currentSite?.platformId && (
                              <a
                                href={`https://app.hubspot.com/blog/${currentSite.platformId}/editor/${rec.targetPostId}/preview`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#3b82f6] hover:text-[#3b82f6]/80 transition-colors"
                                title="Preview in HubSpot"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </td>
                        
                        {/* Anchor Text */}
                        <td className="p-3">
                          <span className="text-xs font-medium">{rec.anchorText}</span>
                        </td>
                        
                        {/* Status */}
                        <td className="p-3 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadge(rec.status)}`}>
                            {rec.status === 'existing' ? 'Verified' : rec.status.charAt(0).toUpperCase() + rec.status.slice(1)}
                          </span>
                        </td>
                        
                        {/* Actions */}
                        <td className="p-3">
                          <div className="flex items-center gap-1 justify-end flex-wrap">
                            {/* Flash confirmation replaces buttons for 1.5s after action */}
                            {rowActionFlash[rec.id] === 'done' && (
                              <span className="px-3 py-1 bg-[#22c55e]/30 text-[#22c55e] rounded text-xs font-semibold flex items-center gap-1 animate-pulse">
                                <Check className="w-3 h-3" /> Applied ✓
                              </span>
                            )}
                            {rowActionFlash[rec.id] === 'dismiss' && (
                              <span className="px-3 py-1 bg-[#ef4444]/20 text-[#ef4444] rounded text-xs font-semibold flex items-center gap-1 animate-pulse">
                                <Trash2 className="w-3 h-3" /> Dismissed
                              </span>
                            )}

                            {/* Normal buttons — hidden during flash */}
                            {!rowActionFlash[rec.id] && (
                              <>
                                {/* Mark Done button - only for pending */}
                                {rec.status === 'pending' && (
                                  <button
                                    onClick={() => handleMarkDone(rec)}
                                    disabled={!!rowActionPending[rec.id]}
                                    className="px-2 py-1 bg-[#22c55e]/20 text-[#22c55e] rounded text-xs hover:bg-[#22c55e]/30 disabled:opacity-60 transition-colors flex items-center gap-1"
                                  >
                                    {rowActionPending[rec.id] === 'done'
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : <Check className="w-3 h-3" />}
                                    Done
                                  </button>
                                )}

                                {/* Dismiss button - only for pending */}
                                {rec.status === 'pending' && (
                                  <button
                                    onClick={() => handleDismiss(rec)}
                                    disabled={!!rowActionPending[rec.id]}
                                    className="px-2 py-1 bg-[#ef4444]/20 text-[#ef4444] rounded text-xs hover:bg-[#ef4444]/30 disabled:opacity-60 transition-colors flex items-center gap-1"
                                  >
                                    {rowActionPending[rec.id] === 'dismiss'
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : <Trash2 className="w-3 h-3" />}
                                    Dismiss
                                  </button>
                                )}

                                {/* Set to Pending button - for applied or dismissed rows */}
                                {(rec.status === 'applied' || rec.status === 'dismissed') && (
                                  <button
                                    onClick={() => handleSetToPending(rec)}
                                    disabled={!!rowActionPending[rec.id]}
                                    className="px-2 py-1 bg-white/10 text-white/60 rounded text-xs hover:bg-white/20 hover:text-white disabled:opacity-60 transition-colors flex items-center gap-1"
                                  >
                                    {rowActionPending[rec.id]
                                      ? <Loader2 className="w-3 h-3 animate-spin" />
                                      : <RotateCcw className="w-3 h-3" />}
                                    Set to Pending
                                  </button>
                                )}

                                {/* Create Link Text (PRO only) */}
                                {isPro && rec.status === 'pending' && (
                                  rec.semanticLinkRequested ? (
                                    <button
                                      onClick={() => handleGenerateSemanticLink(rec)}
                                      className="px-2 py-1 bg-[#ffdc18]/10 text-[#ffdc18] rounded text-xs hover:bg-[#ffdc18]/20 transition-colors flex items-center gap-1"
                                    >
                                      <Eye className="w-3 h-3" />
                                      View Text
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleGenerateSemanticLink(rec)}
                                      className="px-2 py-1 bg-[#ffdc18]/10 text-[#ffdc18] rounded text-xs hover:bg-[#ffdc18]/20 transition-colors flex items-center gap-1"
                                    >
                                      <Sparkles className="w-3 h-3" />
                                      Link Text
                                    </button>
                                  )
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      
                      {/* Reason Row - spans below Relationship through Anchor Text */}
                      {rec.recommendationReason && (
                        <tr className="border-b border-[var(--bg-tertiary)]/50">
                          {/* Direction indicator or checkbox column placeholder */}
                          {postIdFilter ? (
                            <td 
                              className="sticky left-0 z-20"
                              style={{ 
                                backgroundColor: isInbound ? '#3b82f6' : isOutbound ? '#22c55a' : '#6b7280',
                                width: '8px',
                                minWidth: '8px',
                                maxWidth: '8px',
                                padding: 0
                              }}
                            ></td>
                          ) : statusFilter === 'pending' ? (
                            <td className="sticky left-0 z-10 bg-[var(--bg-primary)]"></td>
                          ) : null}
                          {/* Empty cells for Date, Score */}
                          <td></td>
                          <td></td>
                          {/* Empty cells for Impact column(s) */}
                          {postIdFilter ? <td></td> : <><td></td><td></td></>}
                          {/* Reason spans from Relationship through Anchor Text (4 columns) */}
                          <td colSpan={4} className="px-3 pb-3 pt-0">
                            <div className="text-xs text-white/70 italic">
                              {rec.recommendationReason}
                            </div>
                          </td>
                          {/* Empty cells for Status and Actions */}
                          <td></td>
                          <td></td>
                        </tr>
                      )}
                      
                      {/* Expanded Relationship Type Info Row */}
                      {isRelExpanded && (
                        <tr className="border-b border-[var(--bg-tertiary)]">
                          {/* Direction indicator or checkbox column placeholder */}
                          {postIdFilter ? (
                            <td 
                              className="sticky left-0 z-20"
                              style={{ 
                                backgroundColor: isInbound ? '#3b82f6' : isOutbound ? '#22c55a' : '#6b7280',
                                width: '8px',
                                minWidth: '8px',
                                maxWidth: '8px',
                                padding: 0
                              }}
                            ></td>
                          ) : statusFilter === 'pending' ? (
                            <td className="sticky left-0 z-10 bg-[var(--bg-secondary)]"></td>
                          ) : null}
                          <td colSpan={postIdFilter ? 9 : 10} className="p-4 bg-[var(--bg-secondary)]">
                            <div className="max-w-4xl">
                              {relDef ? (
                                <>
                                  <h4 className="text-base font-semibold text-[#ffdc18] mb-2 flex items-center gap-2">
                                    <Info className="w-4 h-4" />
                                    {relDef.title}
                                  </h4>
                                  <p className="text-sm text-white/90 mb-4">{relDef.definition}</p>
                                  
                                  <div className="grid grid-cols-2 gap-4">
                                    {/* When to use */}
                                    <div className="bg-[var(--bg-tertiary)] p-3 rounded border-l-4 border-l-[#ffdc18]">
                                      <p className="text-sm font-medium text-[#ffdc18] mb-1">💡 When to use this link:</p>
                                      <p className="text-sm text-white/80">{relDef.whenToUse}</p>
                                    </div>

                                    {/* Example phrases */}
                                    <div className="bg-[var(--bg-tertiary)] p-3 rounded border-l-4 border-l-[#ffdc18]">
                                      <p className="text-sm font-medium text-[#ffdc18] mb-2">✍️ Example phrases:</p>
                                      <ul className="text-sm text-white/80 space-y-1">
                                        {relDef.examples.map((ex, i) => (
                                          <li key={i}>• {ex}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <h4 className="text-base font-semibold text-[#ffdc18] mb-2 flex items-center gap-2">
                                    <Info className="w-4 h-4" />
                                    {rec.relationshipType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown Relationship'}
                                  </h4>
                                  <p className="text-sm text-white/70">
                                    This is a semantic relationship between posts. The specific type "{rec.relationshipType}" 
                                    indicates how these two pieces of content are connected and why linking them benefits readers.
                                  </p>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Results Info */}
          <div className="p-4 border-t border-[var(--bg-tertiary)] text-sm text-white/70">
            Showing {filteredRecommendations.length} recommendation{filteredRecommendations.length !== 1 ? 's' : ''}
            {statusFilter !== 'all' && ` with status: ${statusFilter}`}
          </div>
        </div>
      )}

      {/* Semantic Link Modal */}
      {semanticModal.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="glass rounded-2xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Semantic Link Placement & Heading Recommendations
              </h3>
              <button
                onClick={() => { frozenRecOrder.current = null; setSemanticModal(prev => ({ ...prev, show: false })); }}
                className="p-1 hover:bg-[var(--bg-tertiary)] rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {semanticModal.loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#ffdc18] animate-spin" />
                <span className="ml-3 text-white/70">Generating link text... (could take 1-2 minutes)</span>
              </div>
            )}

            {semanticModal.error && (
              <div className="p-4 bg-[var(--color-error)]/10 border border-[var(--color-error)]/30 rounded-lg">
                <p className="text-[var(--color-error)]">{semanticModal.error}</p>
              </div>
            )}

            {!semanticModal.loading && !semanticModal.error && semanticModal.recommendation && (
              <div className="space-y-4">
                {/* From/To Posts — HubSpot clients get editor deep-links; HTML clients get direct post URLs */}
                <div className="space-y-1 pb-4 border-b border-[var(--bg-tertiary)]">
                  <div className="flex items-center gap-2">
                    <span className="text-white/70 font-medium">From:</span>
                    <a
                      href={isHtmlClient
                        ? (semanticModal.recommendation.sourcePostUrl || '#')
                        : (currentSite?.platformId
                            ? `https://app.hubspot.com/blog/${currentSite.platformId}/editor/${semanticModal.recommendation.sourcePostId}/content`
                            : '#')
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#ffdc18] hover:underline flex items-center gap-1"
                    >
                      {semanticModal.recommendation.sourcePostTitle}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/70 font-medium">To:</span>
                    <a
                      href={isHtmlClient
                        ? (semanticModal.recommendation.targetPostUrl || '#')
                        : (currentSite?.platformId
                            ? `https://app.hubspot.com/blog/${currentSite.platformId}/editor/${semanticModal.recommendation.targetPostId}/content`
                            : '#')
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#ffdc18] hover:underline flex items-center gap-1"
                    >
                      {semanticModal.recommendation.targetPostTitle}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                {/* ── Heading Structure Analysis ─────────────────────────────── */}

                {/* Failed state — BKA reported structure_analysis_failed */}
                {semanticModal.enableStructureAnalysis && semanticModal.structureAnalysisFailed && !semanticModal.structureAnalysis && (
                  <div className="rounded-xl border border-[#ef4444]/30 overflow-hidden">
                    <div className="p-4 bg-[var(--bg-tertiary)] space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-white">Heading Structure Analysis</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#ef4444]/20 text-[#f87171] border border-[#ef4444]/30">
                          Unavailable
                        </span>
                      </div>
                      <div className="flex items-start gap-2 p-3 bg-[#ef4444]/10 border border-[#ef4444]/20 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-[#f87171] mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-white/80">
                          Heading analysis is temporarily unavailable — your link recommendation is still accurate.
                          Click <strong className="text-white">Re-analyze</strong> to try again next time you generate link text for this post.
                        </p>
                      </div>
                      <div className="flex justify-end">
                        <button
                          onClick={handleReanalyze}
                          className="flex items-center gap-1.5 px-4 py-2 bg-transparent text-white/60 border border-white/20 rounded-lg text-xs font-semibold hover:border-white/40 hover:text-white transition-colors"
                        >
                          <RotateCcw className="w-3 h-3" />
                          Re-analyze next run
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* No-data state — structure enabled but nothing run yet (and no failure) */}
                {semanticModal.enableStructureAnalysis && !semanticModal.structureAnalysis && !semanticModal.structureAnalysisFailed && (
                  <div className="rounded-xl border border-white/10 overflow-hidden">
                    <div className="p-4 bg-[var(--bg-tertiary)] space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-white">Heading Structure Analysis</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-white/10 text-white/50 border border-white/20">
                          Not yet run
                        </span>
                      </div>
                      <p className="text-sm text-white/60">
                        Heading analysis will run automatically the next time link text is generated for this post.
                      </p>
                    </div>
                  </div>
                )}

                {/* Data state — has structure analysis results */}
                {semanticModal.enableStructureAnalysis && semanticModal.structureAnalysis && (() => {
                  const analysis = semanticModal.structureAnalysis;
                  const st = semanticModal.structureState;
                  const isComplete = !!st?.completedAt;
                  const score = analysis.analysis_summary?.overall_score;
                  const recs = analysis.recommendations ?? [];
                  const summary = analysis.analysis_summary;

                  // Count each distinct action type for summary pills
                  const actionCounts: Record<string, number> = {};
                  recs.forEach(r => {
                    const a = r.action?.toUpperCase();
                    if (a && a !== 'KEEP') actionCounts[a] = (actionCounts[a] ?? 0) + 1;
                  });

                  const pendingCount = recs.filter((r, i) => {
                    const actioned = st?.recommendationsApplied.includes(i) || st?.recommendationsSkipped.includes(i) || st?.recommendationsManual.includes(i);
                    return !actioned && r.action?.toLowerCase() !== 'keep';
                  }).length;

                  // Helper: resolve heading tag string from BKA object (uses .tag OR .level)
                  const headingTag = (h: {tag?: string; level?: number} | null | undefined): string => {
                    if (!h) return '?';
                    if (h.tag) return h.tag.toUpperCase();
                    if (h.level) return `H${h.level}`;
                    return '?';
                  };

                  const scoreBg = score === undefined ? '' :
                    score >= 70 ? 'bg-[#22c55e] text-white' :
                    score >= 40 ? 'bg-[#f59e0b] text-white' :
                    'bg-[#ef4444] text-white';

                  const impactStyle = (impact: string | undefined) => {
                    const i = (impact ?? '').toLowerCase();
                    return i === 'high'   ? 'bg-[#ef4444]/20 text-[#f87171] border border-[#ef4444]/30' :
                           i === 'medium' ? 'bg-[#f59e0b]/20 text-[#fbbf24] border border-[#f59e0b]/30' :
                                            'bg-white/10 text-white/50 border border-white/20';
                  };

                  const actionStyle = (a: string) => {
                    switch (a) {
                      case 'REVISE':  return 'bg-[#f59e0b]/20 text-[#fbbf24] border border-[#f59e0b]/40';
                      case 'ADD':     return 'bg-[#06b6d4]/20 text-[#22d3ee] border border-[#06b6d4]/40';
                      case 'ELEVATE': return 'bg-[#3b82f6]/20 text-[#60a5fa] border border-[#3b82f6]/40';
                      case 'DEMOTE':  return 'bg-[#ef4444]/20 text-[#f87171] border border-[#ef4444]/40';
                      case 'KEEP':    return 'bg-[#22c55e]/20 text-[#4ade80] border border-[#22c55e]/40';
                      default:        return 'bg-white/10 text-white/60 border border-white/20';
                    }
                  };

                  // Build frozen rec order once per post (shared by both complete + incomplete branches)
                  const currentPost = semanticModal.sourcePostId ?? '';
                  if (!frozenRecOrder.current || frozenRecOrderPost.current !== currentPost) {
                    const pendingIdxs  = recs.map((rec, idx) => ({ rec, idx }))
                      .filter(({ rec, idx }) => {
                        const isActioned = st?.recommendationsApplied.includes(idx) || st?.recommendationsSkipped.includes(idx) || st?.recommendationsManual.includes(idx);
                        return !isActioned && rec.action?.toLowerCase() !== 'keep';
                      }).map(({ idx }) => idx);
                    const actionedIdxs = recs.map((rec, idx) => ({ rec, idx }))
                      .filter(({ rec, idx }) => {
                        const isActioned = st?.recommendationsApplied.includes(idx) || st?.recommendationsSkipped.includes(idx) || st?.recommendationsManual.includes(idx);
                        return isActioned || rec.action?.toLowerCase() === 'keep';
                      }).map(({ idx }) => idx);
                    frozenRecOrder.current = { pendingIdxs, actionedIdxs };
                    frozenRecOrderPost.current = currentPost;
                  }
                  const { pendingIdxs, actionedIdxs } = frozenRecOrder.current;
                  const pendingRecs  = pendingIdxs.map(idx => ({ rec: recs[idx], idx }));
                  const actionedRecs = actionedIdxs.map(idx => ({ rec: recs[idx], idx }));

                  const renderCard = ({ rec, idx }: { rec: typeof recs[0]; idx: number }) => {
                    const isApplied  = st?.recommendationsApplied.includes(idx);
                    const isSkipped  = st?.recommendationsSkipped.includes(idx);
                    const isManual   = st?.recommendationsManual.includes(idx);
                    const isPending  = pendingHeadingActions.current.has(idx);
                    const hasUndo    = !!st?.undoData?.[String(idx)];
                    const isActioned = isApplied || isSkipped || isManual;
                    const actionType = rec.action?.toUpperCase() ?? 'KEEP';
                    const seoImpact  = (rec as {seo_impact?: string}).seo_impact;

                    return (
                      <div
                        key={idx}
                        className={`p-4 transition-colors ${
                          isApplied ? 'bg-[#22c55e]/5' :
                          isSkipped ? 'bg-white/3 opacity-50' :
                          isManual  ? 'bg-[#8b5cf6]/5' :
                          'bg-[var(--bg-tertiary)]'
                        }`}
                      >
                        {/* Card header: action badge + SEO impact + status */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${actionStyle(actionType)}`}>
                            {actionType === 'ADD' ? '+ ADD' : actionType === 'REVISE' ? '✏ REVISE' : actionType}
                          </span>
                          {seoImpact && (
                            <span className={`ml-auto px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${impactStyle(seoImpact)}`}>
                              {seoImpact} IMPACT
                            </span>
                          )}
                          {isApplied && <span className="ml-auto text-xs text-[#4ade80] font-medium flex items-center gap-1"><Check className="w-3 h-3"/>Applied</span>}
                          {isSkipped && <span className="ml-auto text-xs text-white/40 font-medium">Skipped</span>}
                          {isManual  && <span className="ml-auto text-xs text-[#c084fc] font-medium flex items-center gap-1"><Pencil className="w-3 h-3"/>Manual</span>}
                        </div>

                        {/* Heading content */}
                        <div className="space-y-1 mb-2">
                          {rec.existing_heading && actionType !== 'ADD' && (
                            <p className="text-sm text-white/50">
                              <span className="font-mono text-[11px] bg-white/10 px-1.5 py-0.5 rounded mr-1">
                                {headingTag(rec.existing_heading)}
                              </span>
                              {rec.existing_heading.text ?? ''}
                            </p>
                          )}
                          {rec.recommended && actionType !== 'KEEP' && (
                            <div className="flex items-start gap-2">
                              <p className="text-sm font-medium text-[#ffdc18] flex-1">
                                {actionType !== 'ADD' && <span className="text-white/40 mr-1">→</span>}
                                {actionType === 'ADD' && <span className="text-[#22d3ee] mr-1">+ Add:</span>}
                                <span className="font-mono text-[11px] bg-white/10 px-1.5 py-0.5 rounded mr-1">
                                  {headingTag(rec.recommended)}
                                </span>
                                {rec.recommended.text ?? ''}
                              </p>
                              <button
                                title="Copy heading text (plain text)"
                                onClick={() => {
                                  navigator.clipboard.writeText(rec.recommended?.text ?? '');
                                  setCopiedHeadingIdx(idx);
                                  setTimeout(() => setCopiedHeadingIdx(null), 2000);
                                }}
                                className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors mt-0.5"
                              >
                                {copiedHeadingIdx === idx
                                  ? <><Check className="w-3 h-3 text-[#4ade80]" /><span className="text-[#4ade80]">Copied</span></>
                                  : <><Copy className="w-3 h-3" /><span>Copy</span></>
                                }
                              </button>
                            </div>
                          )}
                          {actionType === 'ADD' && (rec.placement?.insert_before || rec.placement?.paragraph_starts_with) && (
                            <p className="text-xs text-white/40 mt-1">
                              Insert before paragraph:{' '}
                              <em>&ldquo;{String(rec.placement.insert_before || rec.placement.paragraph_starts_with).substring(0, 70)}…&rdquo;</em>
                            </p>
                          )}
                          {rec.reason && (
                            <p className="text-xs text-white/50 italic mt-1">{rec.reason}</p>
                          )}
                        </div>

                        {/* Action buttons — only shown when not actioned AND analysis is not complete */}
                        {!isComplete && actionType !== 'KEEP' && !isActioned && (
                          <div className="mt-3 space-y-2">
                            <div className="flex items-center gap-2">
                              {!isHtmlClient && (
                                <button
                                  onClick={() => handleApplyHeading(idx)}
                                  disabled={isPending}
                                  className="flex items-center gap-1 px-3 py-1.5 bg-[#1e3a2f] text-[#4ade80] border border-[#22c55e]/40 rounded text-xs font-medium hover:bg-[#22c55e]/20 disabled:opacity-50 transition-colors"
                                >
                                  {isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                  Apply
                                </button>
                              )}
                              <button
                                onClick={() => handleManualHeading(idx)}
                                disabled={isPending}
                                className="flex items-center gap-1 px-3 py-1.5 bg-transparent text-white/60 border border-white/20 rounded text-xs font-medium hover:border-white/40 disabled:opacity-50 transition-colors"
                              >
                                <Pencil className="w-3 h-3" />
                                Applied Manually
                              </button>
                              <button
                                onClick={() => handleSkipHeading(idx)}
                                disabled={isPending}
                                className="flex items-center gap-1 px-3 py-1.5 bg-transparent text-white/40 border border-white/10 rounded text-xs font-medium hover:border-white/30 disabled:opacity-50 transition-colors"
                              >
                                Skip
                              </button>
                            </div>
                            {headingApplyErrors[idx] && (
                              <div className="flex items-start gap-1.5 p-2 bg-[#ef4444]/10 border border-[#ef4444]/30 rounded text-xs text-[#f87171]">
                                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span>{headingApplyErrors[idx]}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Undo */}
                        {isApplied && hasUndo && !isComplete && (
                          <button
                            onClick={() => handleUndoHeading(idx)}
                            disabled={pendingHeadingActions.current.has(idx)}
                            className="flex items-center gap-1 mt-2 px-2 py-1 bg-white/5 text-white/40 border border-white/10 rounded text-xs hover:bg-white/10 disabled:opacity-50 transition-colors"
                          >
                            <RotateCcw className="w-3 h-3" /> Undo
                          </button>
                        )}
                        {isApplied && headingApplyErrors[idx] && (
                          <div className="flex items-start gap-1.5 mt-2 p-2 bg-[#ef4444]/10 border border-[#ef4444]/30 rounded text-xs text-[#f87171]">
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>{headingApplyErrors[idx]}</span>
                          </div>
                        )}
                      </div>
                    );
                  };

                  const actionableCount = recs.filter(r => r.action?.toLowerCase() !== 'keep').length;
                  const appliedCount = (st?.recommendationsApplied.length ?? 0) + (st?.recommendationsManual.length ?? 0);

                  const recsListJsx = recs.length > 0 ? (
                    <div className="divide-y divide-white/5">
                      {pendingRecs.map(renderCard)}
                      {actionedRecs.length > 0 && (
                        <>
                          <div className="flex items-center gap-3 px-4 py-3 bg-white/5 border-t-2 border-b border-white/20">
                            <CheckCircle2 className="w-4 h-4 text-[#4ade80] flex-shrink-0" />
                            <span className="text-xs font-bold uppercase tracking-widest text-white/70">
                              Already Applied or Skipped
                            </span>
                            <span className="ml-auto text-xs font-semibold text-white/40 bg-white/10 px-2 py-0.5 rounded-full">
                              {actionedRecs.length}
                            </span>
                          </div>
                          {actionedRecs.map(renderCard)}
                        </>
                      )}
                    </div>
                  ) : null;

                  return (
                    <div className="rounded-xl border border-white/10 overflow-hidden" key={headingActionVersion}>

                      {isComplete ? (
                        /* ── COMPLETE: single collapsed bar, details on expand ── */
                        <>
                          <button
                            onClick={() => setStructureSectionExpanded(v => !v)}
                            className="w-full flex items-center justify-between px-4 py-2.5 bg-[#22c55e]/10 hover:bg-[#22c55e]/15 transition-colors"
                          >
                            <div className="flex items-center gap-2 text-sm text-[#4ade80]">
                              <CheckCircle2 className="w-4 h-4" />
                              Heading Recommendations completed
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/40">
                                {structureSectionExpanded ? 'hide' : 'view details'}
                              </span>
                              {structureSectionExpanded
                                ? <ChevronUp className="w-4 h-4 text-white/40" />
                                : <ChevronDown className="w-4 h-4 text-white/40" />}
                            </div>
                          </button>

                          {structureSectionExpanded && (
                            <>
                              <div className="p-4 space-y-3 bg-[var(--bg-tertiary)] border-t border-white/10">
                                {/* Title + score */}
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold text-white">Heading Structure Analysis</span>
                                  {score !== undefined && (
                                    <span className={`px-2.5 py-0.5 rounded-full text-sm font-bold ${scoreBg}`}>
                                      {score}/100
                                    </span>
                                  )}
                                </div>
                                {/* Stats (BEFORE baseline) */}
                                <div className="space-y-1.5">
                                  <div className="grid grid-cols-4 gap-2 text-center">
                                    {[
                                      { label: 'WORD COUNT',  value: summary?.word_count },
                                      { label: 'RECOMMENDED', value: summary?.recommended_heading_count ?? summary?.recommended_headings },
                                      { label: 'BEFORE',      value: summary?.current_heading_count ?? summary?.current_headings },
                                    ].map(({ label, value }) => (
                                      <div key={label} className="bg-[var(--bg-secondary)] rounded-lg py-2">
                                        <div className="text-[10px] text-white/40 font-medium tracking-wide mb-0.5">{label}</div>
                                        <div className="text-lg font-bold text-white">{value ?? '—'}</div>
                                      </div>
                                    ))}
                                    <div className="bg-[var(--bg-secondary)] rounded-lg py-2">
                                      <div className="text-[10px] text-white/40 font-medium tracking-wide mb-0.5">APPLIED</div>
                                      <div className="text-lg font-bold">
                                        <span className="text-[#4ade80]">{appliedCount}</span>
                                        <span className="text-white/30 text-sm"> / {actionableCount}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <p className="text-[10px] text-white/30 text-center">
                                    Score and heading counts reflect the original baseline before recommendations were applied.
                                  </p>
                                </div>
                                {/* Re-analyze */}
                                <div className="flex justify-end">
                                  <button
                                    onClick={handleReanalyze}
                                    className="flex items-center gap-1 text-xs text-white/60 hover:text-white transition-colors"
                                  >
                                    <RotateCcw className="w-3 h-3" />
                                    Re-analyze next run
                                  </button>
                                </div>
                              </div>
                              {recsListJsx}
                            </>
                          )}
                        </>
                      ) : (
                        /* ── INCOMPLETE: full view with stats, key issues, recs, Mark Complete ── */
                        <>
                          <div className="p-4 space-y-3 bg-[var(--bg-tertiary)]">
                            {/* Title row + score */}
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-white">Heading Structure Analysis</span>
                              {score !== undefined && (
                                <span className={`px-2.5 py-0.5 rounded-full text-sm font-bold ${scoreBg}`}>
                                  {score}/100
                                </span>
                              )}
                            </div>

                            {/* Stats row */}
                            <div className="grid grid-cols-4 gap-2 text-center">
                              {[
                                { label: 'WORD COUNT',  value: summary?.word_count },
                                { label: 'RECOMMENDED', value: summary?.recommended_heading_count ?? summary?.recommended_headings },
                                { label: 'CURRENT',     value: summary?.current_heading_count ?? summary?.current_headings },
                              ].map(({ label, value }) => (
                                <div key={label} className="bg-[var(--bg-secondary)] rounded-lg py-2">
                                  <div className="text-[10px] text-white/40 font-medium tracking-wide mb-0.5">{label}</div>
                                  <div className="text-lg font-bold text-white">{value ?? '—'}</div>
                                </div>
                              ))}
                              <div className="bg-[var(--bg-secondary)] rounded-lg py-2">
                                <div className="text-[10px] text-white/40 font-medium tracking-wide mb-0.5">APPLIED</div>
                                <div className="text-lg font-bold">
                                  <span className={appliedCount > 0 ? 'text-[#4ade80]' : 'text-white'}>{appliedCount}</span>
                                  <span className="text-white/30 text-sm"> / {actionableCount}</span>
                                </div>
                              </div>
                            </div>

                            {/* Action summary badges */}
                            {Object.keys(actionCounts).length > 0 && (
                              <div className="flex items-center gap-2 flex-wrap">
                                {Object.entries(actionCounts).map(([action, count]) => (
                                  <span key={action} className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${actionStyle(action)}`}>
                                    {action === 'ADD'     && <span className="font-bold text-sm leading-none">+</span>}
                                    {action === 'REVISE'  && <Pencil className="w-3 h-3" />}
                                    {action === 'ELEVATE' && <span className="font-bold text-sm leading-none">↑</span>}
                                    {action === 'DEMOTE'  && <span className="font-bold text-sm leading-none">↓</span>}
                                    {count} {action}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Key Issues */}
                            {summary?.key_issues && summary.key_issues.length > 0 && (
                              <div className="border-l-2 border-[#ffdc18] pl-3 space-y-1">
                                <p className="text-xs font-semibold text-[#ffdc18] mb-1">Key Issues:</p>
                                {summary.key_issues.map((issue: string, i: number) => (
                                  <p key={i} className="text-xs text-white/70">{issue}</p>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Collapsible recs toggle */}
                          {recs.length > 0 && (
                            <>
                              <button
                                onClick={() => setStructureSectionExpanded(v => !v)}
                                className="w-full flex items-center justify-between px-4 py-2.5 bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] border-t border-white/10 transition-colors"
                              >
                                <span className="flex items-center gap-2 text-sm font-medium text-white/80">
                                  {structureSectionExpanded
                                    ? <ChevronUp className="w-4 h-4" />
                                    : <ChevronDown className="w-4 h-4" />}
                                  {structureSectionExpanded ? 'Hide' : 'View'} Detailed Recommendations
                                </span>
                                <span className="text-xs text-white/40">({pendingCount} pending)</span>
                              </button>

                              {structureSectionExpanded && recsListJsx}

                              {/* Mark Complete / Skip All */}
                              <div className="px-4 py-3 bg-[var(--bg-secondary)] border-t border-white/10 space-y-2">
                                <p className="text-xs text-white/50 flex items-center gap-1.5">
                                  <span className="text-[#06b6d4]">ℹ</span>
                                  Mark complete to skip structure analysis for this post in future requests.
                                </p>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={handleMarkComplete}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-[#1e3a5f] text-[#60a5fa] border border-[#3b82f6]/40 rounded-lg text-xs font-semibold hover:bg-[#3b82f6]/20 transition-colors"
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    Mark Complete
                                  </button>
                                  <button
                                    onClick={handleSkipAll}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-transparent text-white/50 border border-white/20 rounded-lg text-xs font-semibold hover:border-white/40 transition-colors"
                                  >
                                    Skip All
                                  </button>
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* ── Primary Tag Recommendation ─────────────────────────────── */}
                {semanticModal.enableCategoryRecommendation &&
                  semanticModal.categoryRecommendation &&
                  !semanticModal.structureState?.categoryAcknowledged && (() => {
                    const catRec = semanticModal.categoryRecommendation!;
                    const primaryTag = catRec.recommended_primary || catRec.primary_tag || '';
                    const reason = catRec.reasoning || catRec.reason || '';
                    const scores = catRec.category_fit_scores ?? [];
                    const confidenceColor: Record<string, string> = {
                      high: 'bg-[#22c55e]/20 text-[#4ade80] border-[#22c55e]/40',
                      medium: 'bg-[#ffdc18]/20 text-[#ffdc18] border-[#ffdc18]/40',
                      low: 'bg-white/10 text-white/50 border-white/20',
                    };
                    const confStyle = catRec.confidence ? (confidenceColor[catRec.confidence] ?? confidenceColor.low) : '';
                    return (
                      <div className="border border-[#8b5cf6]/40 rounded-xl overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center gap-2 px-4 py-3 bg-[#8b5cf6]/10">
                          <Tag className="w-4 h-4 text-[#8b5cf6]" />
                          <span className="text-sm font-semibold text-[#8b5cf6]">Primary Tag Recommendation</span>
                          {catRec.confidence && (
                            <span className={`ml-auto px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${confStyle}`}>
                              {catRec.confidence} confidence
                            </span>
                          )}
                        </div>

                        <div className="p-4 space-y-3">
                          {/* Count subtitle */}
                          {scores.length > 0 && (
                            <p className="text-xs text-white/50">
                              This post is assigned to {scores.length} {scores.length === 1 ? 'category' : 'categories'}.
                            </p>
                          )}

                          {/* Category cards with progress bars */}
                          {scores.length > 0 ? (
                            <div className="space-y-2">
                              {scores.map((fs, i) => {
                                const isRecommended = fs.category === primaryTag;
                                const pct = fs.fit_score ?? 0;
                                // Recommended = green; alternatives = blue
                                const barColor = isRecommended ? '#22c55e' : '#3b82f6';
                                const pctColor = isRecommended ? 'text-[#4ade80]' : 'text-[#60a5fa]';
                                return (
                                  <div
                                    key={i}
                                    className={`rounded-lg border p-3 space-y-2 transition-colors ${
                                      isRecommended
                                        ? 'border-[#22c55e]/50 bg-[#22c55e]/5'
                                        : 'border-[#3b82f6]/30 bg-[#3b82f6]/5'
                                    }`}
                                  >
                                    {/* Name row */}
                                    <div className="flex items-center gap-2">
                                      {/* Radio indicator */}
                                      <div className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                        isRecommended ? 'border-[#22c55e]' : 'border-[#3b82f6]/60'
                                      }`}>
                                        {isRecommended && <div className="w-1.5 h-1.5 rounded-full bg-[#22c55e]" />}
                                      </div>
                                      <span className={`text-sm font-medium ${isRecommended ? 'text-white' : 'text-white/80'}`}>
                                        {fs.category}
                                      </span>
                                      {isRecommended && (
                                        <span className="flex items-center gap-0.5 text-[10px] font-bold text-[#4ade80] uppercase tracking-wide">
                                          ← Recommended
                                        </span>
                                      )}
                                    </div>
                                    {/* Progress bar */}
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                        <div
                                          className="h-full rounded-full transition-all"
                                          style={{ width: `${pct}%`, backgroundColor: barColor }}
                                        />
                                      </div>
                                      <span className={`text-xs font-bold w-8 text-right ${pctColor}`}>
                                        {fs.fit_score !== undefined ? `${pct}%` : '—'}
                                      </span>
                                    </div>
                                    {/* Per-category reasoning */}
                                    {fs.fit_reasoning && (
                                      <p className="text-xs text-white/50 italic leading-relaxed">
                                        {fs.fit_reasoning}
                                      </p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            /* Fallback when no category_fit_scores */
                            <p className="text-sm text-white/80">
                              BKA recommends{' '}
                              <strong className="text-[#c4b5fd]">&ldquo;{primaryTag}&rdquo;</strong>{' '}
                              as the primary tag for this post.
                            </p>
                          )}

                          {/* Overall analysis box */}
                          {reason && (
                            <div className="p-3 bg-[var(--bg-secondary)] rounded-lg border border-white/10">
                              <p className="text-xs text-white/50 font-semibold mb-1">Analysis:</p>
                              <p className="text-xs text-white/70 leading-relaxed">{reason}</p>
                            </div>
                          )}

                          {/* Actions — Set as Primary hidden for HTML/No-CMS clients */}
                          <div className="flex items-center gap-2 pt-1">
                            {!isHtmlClient && (
                              <button
                                onClick={() => setShowCategoryConfirm(true)}
                                disabled={categoryApplying}
                                className="flex items-center gap-1.5 px-4 py-2 bg-[#22c55e] text-white rounded-lg text-xs font-semibold hover:bg-[#16a34a] disabled:opacity-50 transition-colors"
                              >
                                {categoryApplying
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <Check className="w-3 h-3" />}
                                Set as Primary
                              </button>
                            )}
                            <button
                              onClick={() => handleAckCategory('dismiss')}
                              disabled={categoryApplying}
                              className="flex items-center gap-1.5 px-4 py-2 bg-transparent text-white/60 border border-white/20 rounded-lg text-xs font-semibold hover:border-white/40 hover:text-white disabled:opacity-50 transition-colors"
                            >
                              Keep Current
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                {/* Primary tag already acknowledged */}
                {semanticModal.enableCategoryRecommendation &&
                  semanticModal.categoryRecommendation &&
                  semanticModal.structureState?.categoryAcknowledged && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-[#8b5cf6]/10 border border-[#8b5cf6]/30 rounded-xl text-sm text-[#c4b5fd]">
                    <CheckCircle2 className="w-4 h-4" />
                    Primary tag recommendation acknowledged
                  </div>
                )}

                {/* Category confirmation dialog */}
                {showCategoryConfirm && semanticModal.categoryRecommendation && (
                  <div className="border border-[#8b5cf6]/50 bg-[#8b5cf6]/10 rounded-xl p-4 space-y-3">
                    <p className="text-sm font-medium">
                      Apply Primary Tag Recommendation?
                    </p>
                    <p className="text-xs text-white/70">
                      This will keep{' '}
                      <strong className="text-[#c4b5fd]">
                        &ldquo;{semanticModal.categoryRecommendation.recommended_primary || semanticModal.categoryRecommendation.primary_tag}&rdquo;
                      </strong>{' '}
                      as the only topical tag on this post and remove all other non-structural tags.
                      Structural tags will not be affected.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleAckCategory('apply')}
                        className="px-3 py-1.5 bg-[#8b5cf6] text-white rounded text-xs font-medium hover:bg-[#7c3aed] transition-colors"
                      >
                        Confirm — Set as Primary
                      </button>
                      <button
                        onClick={() => setShowCategoryConfirm(false)}
                        className="px-3 py-1.5 bg-[var(--bg-tertiary)] text-white/70 rounded text-xs font-medium hover:bg-[var(--bg-secondary)] transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Link Text Recommendation & Placement ──────────────────── */}
                <div className="flex items-center gap-3 pt-1">
                  <Link2 className="w-4 h-4 text-[#60a5fa] flex-shrink-0" />
                  <span className="text-sm font-semibold text-white">Link Text Recommendation &amp; Placement</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                {/* Optimal Placement */}
                <div className="pb-4 border-b border-[var(--bg-tertiary)]">
                  <div className="text-sm font-medium text-white/70 mb-2">📍 Optimal Placement:</div>
                  <span className="inline-block px-3 py-1.5 bg-[var(--bg-tertiary)] rounded text-sm font-medium">
                    {semanticModal.recommendation.optimalPlacement || 'Not specified'}
                  </span>
                </div>

                {/* Why This Works */}
                {semanticModal.recommendation.placementRationale && (
                  <div className="pb-4 border-b border-[var(--bg-tertiary)]">
                    <div className="text-sm font-medium text-white/70 mb-2">💡 Why This Works:</div>
                    <div className="p-3 bg-[var(--bg-tertiary)] rounded-lg border-l-4 border-l-[#ffdc18]">
                      <p className="text-sm text-white/90">
                        {semanticModal.recommendation.placementRationale}
                      </p>
                    </div>
                  </div>
                )}

                {/* Current Paragraph */}
                {semanticModal.recommendation.currentParagraph && (
                  <div className="pb-4 border-b border-[var(--bg-tertiary)]">
                    <div className="text-sm font-medium text-white/70 mb-2">📄 Current Paragraph:</div>
                    <div 
                      className="p-3 bg-[var(--bg-tertiary)] rounded-lg text-sm [&_*]:!text-white/90"
                      dangerouslySetInnerHTML={{ 
                        __html: semanticModal.recommendation.currentParagraph 
                      }}
                    />
                  </div>
                )}

                {/* Recommended Replacement */}
                {(() => {
                  const targetUrl = semanticModal.recommendation.targetPostUrl || '';
                  const replacementHtml = buildReplacementHTML(
                    semanticModal.recommendation.recommendedReplacement,
                    semanticModal.recommendation.anchorText,
                    targetUrl
                  );
                  return (
                    <div className="pb-4 border-b border-[var(--bg-tertiary)]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-white/70">✨ Recommended Replacement:</span>
                        <button
                          onClick={() => {
                            const container = document.getElementById('vzx-recommended-replacement');
                            if (container) {
                              const htmlContent = container.innerHTML;
                              const plainText = container.innerText;
                              try {
                                const blob = new Blob([htmlContent], { type: 'text/html' });
                                const blobPlain = new Blob([plainText], { type: 'text/plain' });
                                const clipboardItem = new ClipboardItem({
                                  'text/html': blob,
                                  'text/plain': blobPlain
                                });
                                navigator.clipboard.write([clipboardItem]);
                                const btn = document.getElementById('copy-html-btn');
                                if (btn) {
                                  btn.textContent = '✓ Copied!';
                                  setTimeout(() => { btn.textContent = '📋 Copy HTML'; }, 2000);
                                }
                              } catch {
                                navigator.clipboard.writeText(plainText);
                              }
                            }
                          }}
                          id="copy-html-btn"
                          className="px-3 py-1.5 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-secondary)] rounded text-sm font-medium transition-colors flex items-center gap-1"
                        >
                          📋 Copy HTML
                        </button>
                      </div>
                      <div 
                        id="vzx-recommended-replacement"
                        className="p-3 bg-[#22c55a]/10 border border-[#22c55a]/30 rounded-lg text-sm [&_*]:!text-white/90 [&_a]:!text-[#ffdc18] [&_a]:!underline"
                        dangerouslySetInnerHTML={{ __html: replacementHtml }}
                      />
                      <div className="mt-3 p-3 bg-[#3b82f6]/10 border border-[#3b82f6]/40 rounded-lg flex items-start gap-2.5">
                        <span className="text-lg leading-none flex-shrink-0">📋</span>
                        <p className="text-sm font-semibold text-[#93c5fd] leading-snug">
                          Paste into HubSpot editor to replace the current paragraph — the link will be preserved!
                        </p>
                      </div>
                      <div className="mt-2 p-3 bg-[#f59e0b]/10 border border-[#f59e0b]/40 rounded-lg flex items-start gap-2.5">
                        <span className="text-lg leading-none flex-shrink-0">⚠️</span>
                        <p className="text-sm font-medium text-[#fcd34d] leading-snug">
                          Once you insert the link recommendation, make sure you <strong className="text-white">update the post</strong> to take all of the changes live.
                        </p>
                      </div>
                    </div>
                  );
                })()}

                {/* Note with anchor text and URL */}
                {semanticModal.recommendation.anchorText && (
                  <div className="p-3 bg-[#ffdc18]/10 border border-[#ffdc18]/30 rounded-lg">
                    <p className="text-sm">
                      <strong className="text-white">Note:</strong>{' '}
                      <span className="text-white/70">Wrap &ldquo;</span>
                      <em className="text-[#ffdc18]">{semanticModal.recommendation.anchorText}</em>
                      <span className="text-white/70">&rdquo; in a link to: </span>
                      <code className="text-xs bg-[var(--bg-tertiary)] px-2 py-0.5 rounded break-all">
                        {semanticModal.recommendation.targetPostUrl || 'target post'}
                      </code>
                    </p>
                  </div>
                )}

                {/* Close button */}
                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => { frozenRecOrder.current = null; setSemanticModal(prev => ({ ...prev, show: false })); }}
                    className="px-4 py-2 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-secondary)] rounded-lg text-sm font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
